import React, { useState, useCallback } from 'react';
import useSWR, { mutate } from 'swr';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert,
  Tabs, Tab, Chip, Stack, TextField, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl,
  InputLabel, IconButton, Tooltip, Divider, Card, CardContent,
  Slider, Checkbox, TableSortLabel,
} from '@mui/material';
import {
  CheckCircle as ConfirmIcon,
  Cancel as RejectIcon,
  AddCircle as GrantIcon,
  InfoOutlined as InfoIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  VerifiedUser as VerifiedOrgIcon,
  GppBad as UnverifiedOrgIcon,
  AutoFixHigh as RecalcIcon,
  RemoveCircle as RevokeIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { fetcher, axiosService } from 'shared/helpers/axios';
import BackButton from 'components/common/buttons/BackButton';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import PageContainer from 'components/common/layout/PageContainer';

// ── helpers ───────────────────────────────────────────────────────────────────

const fmt = (cents) => `€${(cents / 100).toFixed(2)}`;
const fmtUSD = (cents) => `$${(cents / 100).toFixed(2)}`;

const StatusChip = ({ status }) => {
  const map = {
    pending: 'warning',
    transfer_submitted: 'info',
    completed: 'success',
    failed: 'error',
    refunded: 'default',
  };
  return <Chip label={status} color={map[status] || 'default'} size="small" />;
};

const StatCard = ({ label, value, sub, color }) => (
  <Card variant="outlined" sx={{ flex: 1, minWidth: { xs: '45%', sm: 'auto' } }}>
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="h4" fontWeight={700} color={color}>{value ?? '–'}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

// ── shared constants ─────────────────────────────────────────────────────────

const WORDS_PER_PAGE    = 500;    // standard academic page (~500 words)
const TOKENS_PER_WORD   = 4 / 3; // ~1.33 tokens per English word
const WORDS_PER_SECTION = 800;    // standard full proposal section
const WORDS_PER_ABSTRACT = 300;   // short abstract

// ── credit value calculator ───────────────────────────────────────────────────

const calcCreditValue = ({ wordsPerCredit, outputRateCents }) => {
  const outputTokens = Math.round(wordsPerCredit * TOKENS_PER_WORD);
  const costCents = outputTokens * outputRateCents / 1_000_000;
  const recommendedCents = Math.max(1, Math.ceil(costCents));
  return { outputTokens, costCents, recommendedCents };
};

// ── token/word/page explanation helper ───────────────────────────────────────

const genCost = (words, outputRate) => {
  const outTokens = Math.round(words * TOKENS_PER_WORD);
  return { outTokens, costCents: outTokens * outputRate / 1_000_000 };
};

const tokenExplanation = (cfg) => {
  if (!cfg) return null;
  const {
    credit_value_cents      = 1,
    output_cost_per_m_cents = 1000,
    words_per_credit        = 500,
    prompt_overhead_tokens  = 150,
  } = cfg;

  const credit   = genCost(words_per_credit,    output_cost_per_m_cents);
  const section  = genCost(WORDS_PER_SECTION,   output_cost_per_m_cents);
  const abstract = genCost(WORDS_PER_ABSTRACT,  output_cost_per_m_cents);

  return {
    creditValueCents: credit_value_cents,
    creditValueUSD:   fmtUSD(credit_value_cents),
    // per-credit reference
    wordsPerCredit:   words_per_credit,
    tokensPerCredit:  credit.outTokens,
    overheadTokens:   prompt_overhead_tokens,
    costCents:        credit.costCents.toFixed(4),
    pages:            (words_per_credit / WORDS_PER_PAGE).toFixed(1),
    // section
    wordsPerSection:  WORDS_PER_SECTION,
    tokensPerSection: section.outTokens,
    sectionCostCents: section.costCents.toFixed(4),
    creditsPerSection: Math.max(1, Math.ceil(section.costCents / credit_value_cents)),
    // abstract
    wordsPerAbstract:  WORDS_PER_ABSTRACT,
    tokensPerAbstract: abstract.outTokens,
    abstractCostCents: abstract.costCents.toFixed(4),
    creditsPerAbstract: Math.max(1, Math.ceil(abstract.costCents / credit_value_cents)),
    // kept for Pricing tab compatibility
    outputTokens:       credit.outTokens,
    abstractCredits:    Math.max(1, Math.ceil(abstract.costCents / credit_value_cents)),
    fullSectionCredits: Math.max(1, Math.ceil(section.costCents / credit_value_cents)),
  };
};

// ── Grant credits dialog ──────────────────────────────────────────────────────

const GrantDialog = ({ open, user, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [grantType, setGrantType] = useState('bonus');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { showSnackbar } = useSnackbar();

  const handleSubmit = async () => {
    if (!amount || parseInt(amount) <= 0) return;
    setSaving(true);
    setError(null);
    try {
      await axiosService.post('/billing/admin/grant/', {
        user_id: user.user_id,
        amount: parseInt(amount),
        type: grantType,
        description: description || `Admin grant (${grantType})`,
      });
      showSnackbar(`Granted ${amount} credits to ${user.name}`);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to grant credits');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Grant Credits — {user?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Credits to grant"
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            slotProps={{ htmlInput: { min: 1 } }}
            required
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={grantType} onChange={e => setGrantType(e.target.value)} label="Type">
              <MenuItem value="bonus">Bonus</MenuItem>
              <MenuItem value="adjustment">Adjustment</MenuItem>
              <MenuItem value="refund">Refund</MenuItem>
              <MenuItem value="purchase">Purchase</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Description (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !amount || parseInt(amount) <= 0}
        >
          {saving ? <CircularProgress size={20} /> : 'Grant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Revoke credits dialog ─────────────────────────────────────────────────────

const RevokeDialog = ({ open, user, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { showSnackbar } = useSnackbar();

  const maxRevoke = user?.balance ?? 0;

  const handleSubmit = async () => {
    const n = parseInt(amount);
    if (!n || n <= 0) return;
    setSaving(true);
    setError(null);
    try {
      await axiosService.post('/billing/admin/revoke/', {
        user_id: user.user_id,
        amount: n,
        description: description || `Admin revocation`,
      });
      showSnackbar(`Revoked ${n} credit(s) from ${user.name}`);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke credits');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>Revoke Credits — {user?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            Current balance: <strong>{maxRevoke}</strong> credit(s).
            This deducts credits and is recorded in the audit log.
          </Alert>
          <TextField
            label="Credits to revoke"
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            slotProps={{ htmlInput: { min: 1, max: maxRevoke } }}
            helperText={`Max: ${maxRevoke}`}
            required
            fullWidth
          />
          <TextField
            label="Reason (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={saving || !amount || parseInt(amount) <= 0 || parseInt(amount) > maxRevoke}
        >
          {saving ? <CircularProgress size={20} /> : 'Revoke'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Reset signup bonus dialog ─────────────────────────────────────────────────

const FullResetDialog = ({ open, onClose, onSuccess }) => {
  const [confirm, setConfirm] = useState('');
  const [dryRunResult, setDryRunResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showSnackbar } = useSnackbar();

  const CONFIRM_PHRASE = 'RESET ALL CREDITS';

  const runDryRun = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosService.post('/billing/admin/full_reset/', { dry_run: true });
      setDryRunResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    }
    setLoading(false);
  };

  const handleReset = async () => {
    if (confirm !== CONFIRM_PHRASE) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosService.post('/billing/admin/full_reset/', { dry_run: false });
      showSnackbar(`Full reset complete — deleted ${res.data.transactions_deleted} transaction(s), zeroed ${res.data.accounts_zeroed} account(s)`);
      onSuccess();
      onClose();
      setDryRunResult(null);
      setConfirm('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    }
    setLoading(false);
  };

  const handleClose = () => { setDryRunResult(null); setError(null); setConfirm(''); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>Full Credit Reset</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="error" icon={false}>
            <Typography fontWeight={700} mb={0.5}>⚠ Irreversible destructive action</Typography>
            This will permanently <strong>delete every CreditTransaction record</strong> and
            set every account balance to zero. Use this only to start fresh before
            re-granting bonuses via the management command.
          </Alert>
          {!dryRunResult ? (
            <Typography variant="body2" color="text.secondary">
              Run a dry-run first to see how many records will be affected.
            </Typography>
          ) : (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Dry-run preview</Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">Transactions to delete: <strong>{dryRunResult.transactions_deleted}</strong></Typography>
                <Typography variant="body2">Accounts to zero: <strong>{dryRunResult.accounts_zeroed}</strong></Typography>
                <Typography variant="body2" color="text.secondary">Currently non-zero accounts: {dryRunResult.non_zero_accounts}</Typography>
              </Stack>
            </Paper>
          )}
          {dryRunResult && (
            <TextField
              label={`Type "${CONFIRM_PHRASE}" to confirm`}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              size="small"
              fullWidth
              error={confirm.length > 0 && confirm !== CONFIRM_PHRASE}
            />
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        {!dryRunResult ? (
          <Button onClick={runDryRun} variant="outlined" color="warning" disabled={loading}>
            {loading ? <CircularProgress size={16} /> : 'Preview (dry run)'}
          </Button>
        ) : (
          <Button
            onClick={handleReset}
            variant="contained"
            color="error"
            disabled={loading || confirm !== CONFIRM_PHRASE}
          >
            {loading ? <CircularProgress size={16} /> : 'Delete All & Reset'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const ResetBonusDialog = ({ open, onClose, onSuccess }) => {
  const [dryRunResult, setDryRunResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showSnackbar } = useSnackbar();

  const runDryRun = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosService.post('/billing/admin/reset_signup_bonus/', { dry_run: true });
      setDryRunResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    }
    setLoading(false);
  };

  const handleReset = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosService.post('/billing/admin/reset_signup_bonus/', { dry_run: false });
      showSnackbar(`Revoked signup bonuses from ${res.data.users_affected} user(s) — ${res.data.credits_revoked} credits total`);
      onSuccess();
      onClose();
      setDryRunResult(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    }
    setLoading(false);
  };

  const handleClose = () => { setDryRunResult(null); setError(null); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>Reset All Signup Bonuses</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="error">
            This will deduct the signup bonus credits from every user who still has them.
            Users who already spent their bonus are unaffected. This action is recorded in the audit log.
          </Alert>
          {!dryRunResult && (
            <Typography variant="body2" color="text.secondary">
              Run a dry-run first to preview how many users and credits are affected.
            </Typography>
          )}
          {dryRunResult && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Dry-run preview</Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">Users affected: <strong>{dryRunResult.users_affected}</strong></Typography>
                <Typography variant="body2">Credits to revoke: <strong>{dryRunResult.credits_revoked}</strong></Typography>
                <Typography variant="body2" color="text.secondary">Users skipped (zero balance): {dryRunResult.users_skipped_zero_balance}</Typography>
              </Stack>
              {dryRunResult.preview?.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>Sample (up to 10):</Typography>
                  {dryRunResult.preview.map((p, i) => (
                    <Typography key={i} variant="caption" color="text.secondary" display="block">
                      {p.user} — balance {p.current_balance} → revoke {p.credits_to_revoke}
                    </Typography>
                  ))}
                </Box>
              )}
            </Paper>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={runDryRun} disabled={loading} variant="outlined">
          {loading && !dryRunResult ? <CircularProgress size={20} /> : 'Preview (Dry Run)'}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleReset}
          disabled={loading || !dryRunResult || dryRunResult.users_affected === 0}
        >
          {loading && dryRunResult ? <CircularProgress size={20} /> : 'Confirm Reset'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Bulk revoke dialog ────────────────────────────────────────────────────────

const BulkRevokeDialog = ({ open, users, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { showSnackbar } = useSnackbar();

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        user_ids: users.map(u => u.user_id),
        description: description || 'Admin bulk credit revocation',
      };
      if (amount) payload.amount = parseInt(amount);
      const res = await axiosService.post('/billing/admin/revoke_bulk/', payload);
      showSnackbar(`Revoked ${res.data.total_revoked} credit(s) from ${res.data.processed} user(s)`);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke credits');
    }
    setSaving(false);
  };

  const totalBalance = users.reduce((s, u) => s + (u.balance || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>
        Bulk Revoke — {users.length} user{users.length !== 1 ? 's' : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            Selected users hold <strong>{totalBalance}</strong> credit(s) in total.
            Leave <em>Credits</em> blank to revoke each user's full balance.
          </Alert>
          <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 160, overflowY: 'auto' }}>
            {users.map(u => (
              <Stack key={u.user_id} direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">{u.name}</Typography>
                <Chip label={u.balance} size="small" color={u.balance > 0 ? 'warning' : 'default'} />
              </Stack>
            ))}
          </Paper>
          <TextField
            label="Credits to revoke per user (blank = full balance)"
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            slotProps={{ htmlInput: { min: 1 } }}
            fullWidth
          />
          <TextField
            label="Reason (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={saving || users.length === 0}
        >
          {saving ? <CircularProgress size={20} /> : `Revoke from ${users.length} user${users.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Package editor row ────────────────────────────────────────────────────────

const proportionalCredits = (priceCents, creditValueCents) =>
  creditValueCents > 0 ? Math.round(priceCents / creditValueCents) : 0;

const discountPct = (originalCents, saleCents) =>
  originalCents > saleCents && originalCents > 0
    ? Math.round((1 - saleCents / originalCents) * 100)
    : 0;

const PackageRow = ({ pkg, onChange, onDelete, creditValueCents }) => {
  const propCredits = proportionalCredits(pkg.price_cents, creditValueCents);
  const mismatch = propCredits > 0 && pkg.credits !== propCredits;
  const discount = discountPct(pkg.original_price_cents, pkg.price_cents);

  return (
    <Stack spacing={0.5} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 2 }}>
          <TextField size="small" label="ID" value={pkg.id} onChange={e => onChange({ ...pkg, id: e.target.value })} fullWidth />
        </Box>
        <Box sx={{ flex: 2 }}>
          <TextField size="small" label="Label" value={pkg.label} onChange={e => onChange({ ...pkg, label: e.target.value })} fullWidth />
        </Box>
        <Box sx={{ flex: 1.5 }}>
          <TextField
            size="small"
            label="Credits"
            type="number"
            value={pkg.credits}
            onChange={e => onChange({ ...pkg, credits: parseInt(e.target.value) || 0 })}
            fullWidth
            color={mismatch ? 'warning' : undefined}
            helperText={mismatch ? `Prop: ${propCredits}` : undefined}
            slotProps={{
              input: mismatch ? {
                endAdornment: (
                  <Tooltip title={`Set to proportional (${propCredits})`}>
                    <IconButton size="small" onClick={() => onChange({ ...pkg, credits: propCredits })} tabIndex={-1}>
                      <RecalcIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                ),
              } : undefined,
            }}
          />
        </Box>
        <Box sx={{ flex: 2 }}>
          <TextField
            size="small"
            label="Sale Price (cents)"
            type="number"
            value={pkg.price_cents}
            onChange={e => {
              const price = parseInt(e.target.value) || 0;
              onChange({ ...pkg, price_cents: price, credits: proportionalCredits(price, creditValueCents) || pkg.credits });
            }}
            fullWidth
            color={discount > 0 ? 'success' : undefined}
            helperText={discount > 0 ? `${discount}% off` : undefined}
            slotProps={{ htmlInput: { min: 0 } }}
          />
        </Box>
        <Box sx={{ flex: 2 }}>
          <TextField
            size="small"
            label="Original Price (cents, optional)"
            type="number"
            value={pkg.original_price_cents || ''}
            placeholder="Leave empty = no discount"
            onChange={e => {
              const val = e.target.value === '' ? undefined : parseInt(e.target.value) || 0;
              onChange({ ...pkg, original_price_cents: val });
            }}
            fullWidth
            helperText={pkg.original_price_cents ? `€${(pkg.original_price_cents / 100).toFixed(2)} → strikethrough` : 'No discount'}
            slotProps={{ htmlInput: { min: 0 } }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextField size="small" label="Currency" value={pkg.currency} onChange={e => onChange({ ...pkg, currency: e.target.value })} fullWidth />
        </Box>
        <Box sx={{ flex: 2.5 }}>
          <TextField size="small" label="Description" value={pkg.description} onChange={e => onChange({ ...pkg, description: e.target.value })} fullWidth />
        </Box>
        <Box sx={{ flex: 'none', pt: 0.5 }}>
          <Tooltip title="Remove package">
            <IconButton size="small" color="error" onClick={onDelete}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </Stack>
      {discount > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 0.5 }}>
          <Chip label={`-${discount}% discount active`} size="small" color="success" />
          <Typography variant="caption" color="text.secondary">
            Users see: <s>€{(pkg.original_price_cents / 100).toFixed(2)}</s> → €{(pkg.price_cents / 100).toFixed(2)}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const AdminAICreditsPage = () => {
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [grantTarget, setGrantTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [resetBonusOpen, setResetBonusOpen] = useState(false);
  const [fullResetOpen, setFullResetOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkRevokeOpen, setBulkRevokeOpen] = useState(false);
  const [sortField, setSortField] = useState('balance');
  const [sortDir, setSortDir] = useState('desc');
  const [configDraft, setConfigDraft] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [profitVat, setProfitVat] = useState(20);
  const [profitIncomeTax, setProfitIncomeTax] = useState(0);
  const [profitOverhead, setProfitOverhead] = useState(0);
  const [profitInputTokens, setProfitInputTokens] = useState(2000);
  const { showSnackbar } = useSnackbar();

  const { data: overview, isLoading: overviewLoading } = useSWR('/billing/admin/overview/', fetcher);
  const { data: usersData, isLoading: usersLoading, mutate: mutateUsers } = useSWR(
    `/billing/admin/users/?search=${encodeURIComponent(search)}`, fetcher
  );
  const { data: pendingData, isLoading: pendingLoading, mutate: mutatePending } = useSWR(
    '/billing/admin/pending/', fetcher
  );
  const { data: cfgData, isLoading: cfgLoading, mutate: mutateCfg } = useSWR(
    '/billing/admin/config/', fetcher
  );

  const users = usersData?.results || [];
  const pending = pendingData?.results || [];
  const cfg = configDraft ?? cfgData;
  const explanation = tokenExplanation(cfg);

  const initConfigEdit = useCallback(() => {
    if (cfgData && !configDraft) setConfigDraft({ ...cfgData, packages: cfgData.packages ? [...cfgData.packages] : [] });
  }, [cfgData, configDraft]);

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await axiosService.put('/billing/admin/config/', configDraft);
      showSnackbar('Configuration saved');
      setConfigDraft(null);
      mutateCfg();
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to save configuration', 'error');
    }
    setSavingConfig(false);
  };

  const handleConfirm = async (id) => {
    try {
      await axiosService.post('/billing/admin/confirm_transfer/', { purchase_id: id });
      showSnackbar('Purchase confirmed — credits added');
      mutatePending();
      mutate('/billing/admin/overview/');
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to confirm', 'error');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this transfer request?')) return;
    try {
      await axiosService.post('/billing/admin/reject_transfer/', { purchase_id: id });
      showSnackbar('Transfer rejected');
      mutatePending();
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to reject', 'error');
    }
  };

  if (overviewLoading && tab === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      </Container>
    );
  }

  return (
    <PageContainer width="wide">
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <BackButton position="inline" />
        <Typography variant="h4" fontWeight={800}>AI Credits Administration</Typography>
        {overview?.pending_purchases_count > 0 && (
          <Chip label={`${overview.pending_purchases_count} pending`} color="warning" />
        )}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Users" />
        <Tab label={`Pending Purchases${overview?.pending_purchases_count ? ` (${overview.pending_purchases_count})` : ''}`} />
        <Tab label="Pricing & Packages" />
        <Tab label="Credit Value" />
        <Tab label="Profit Calculator" />
      </Tabs>

      {/* ── Overview ── */}
      {tab === 0 && (
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <StatCard label="Users with Accounts" value={overview?.total_users_with_accounts} />
            <StatCard label="Users with Credits" value={overview?.users_with_credits} color="success.main" />
            <StatCard label="Credits in Circulation" value={overview?.total_credits_in_circulation} color="primary.main" />
            <StatCard label="Total Credits Issued" value={overview?.total_credits_issued} />
            <StatCard label="Credits Used (AI)" value={overview?.total_credits_consumed} color="error.main" />
            {(overview?.total_credits_revoked > 0) && (
              <StatCard label="Credits Revoked" value={overview?.total_credits_revoked} color="warning.main" />
            )}
          </Stack>

          {/* Admin actions */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Admin Actions</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Tooltip title="Revoke the signup welcome bonus from all users who still have those credits. Users who already spent them are unaffected.">
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<ResetIcon />}
                  onClick={() => setResetBonusOpen(true)}
                >
                  Reset All Signup Bonuses
                </Button>
              </Tooltip>
              <Tooltip title="Permanently delete ALL credit transactions and zero every balance. Use before re-granting bonuses from scratch via the management command.">
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setFullResetOpen(true)}
                >
                  Full Credit Reset
                </Button>
              </Tooltip>
            </Stack>
          </Paper>

          {overview?.pending_purchases_count > 0 && (
            <Alert severity="warning" action={
              <Button size="small" onClick={() => setTab(2)}>Review</Button>
            }>
              {overview.pending_purchases_count} purchase{overview.pending_purchases_count > 1 ? 's' : ''} awaiting approval
              {overview.pending_amount_cents > 0 && ` — total ${fmt(overview.pending_amount_cents)}`}
            </Alert>
          )}

          {explanation && (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                <InfoIcon color="info" />
                <Typography variant="h6" fontWeight={600}>What is 1 Otable Credit?</Typography>
              </Stack>

              {/* Key metrics row */}
              <Stack direction="row" spacing={3} flexWrap="wrap" mb={2.5}>
                {[
                  { label: 'Credit value',      value: explanation.creditValueUSD },
                  { label: 'Words per credit',   value: explanation.wordsPerCredit.toLocaleString() + ' words' },
                  { label: 'Tokens per credit',  value: explanation.tokensPerCredit.toLocaleString() + ' tokens' },
                  { label: 'Words per section',  value: explanation.wordsPerSection + ' words' },
                  { label: 'Tokens per section', value: explanation.tokensPerSection.toLocaleString() + ' tokens' },
                  { label: 'Credits per section',value: explanation.creditsPerSection + ' credit' + (explanation.creditsPerSection !== 1 ? 's' : '') },
                ].map(({ label, value }) => (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                    <Typography variant="body2" fontWeight={700}>{value}</Typography>
                  </Box>
                ))}
              </Stack>

              {/* Generation comparison table */}
              <Typography variant="subtitle2" fontWeight={600} mb={1}>Cost per generation type</Typography>
              <TableContainer component={Box} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
                      <TableCell>Generation</TableCell>
                      <TableCell align="right">Words</TableCell>
                      <TableCell align="right">Output Tokens</TableCell>
                      <TableCell align="right">API Cost</TableCell>
                      <TableCell align="right">Credits Charged</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: 'primary.50' }}>
                      <TableCell><strong>1-credit reference</strong></TableCell>
                      <TableCell align="right">{explanation.wordsPerCredit.toLocaleString()}</TableCell>
                      <TableCell align="right">{explanation.tokensPerCredit.toLocaleString()}</TableCell>
                      <TableCell align="right">{explanation.costCents}¢</TableCell>
                      <TableCell align="right"><Chip label="1 credit" size="small" color="primary" /></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Abstract (~{explanation.wordsPerAbstract} words)</TableCell>
                      <TableCell align="right">{explanation.wordsPerAbstract}</TableCell>
                      <TableCell align="right">{explanation.tokensPerAbstract.toLocaleString()}</TableCell>
                      <TableCell align="right">{explanation.abstractCostCents}¢</TableCell>
                      <TableCell align="right">
                        <Chip label={`${explanation.creditsPerAbstract} credit${explanation.creditsPerAbstract !== 1 ? 's' : ''}`} size="small"
                          color={explanation.creditsPerAbstract === 1 ? 'success' : 'warning'} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Full section (~{explanation.wordsPerSection} words)</TableCell>
                      <TableCell align="right">{explanation.wordsPerSection}</TableCell>
                      <TableCell align="right">{explanation.tokensPerSection.toLocaleString()}</TableCell>
                      <TableCell align="right">{explanation.sectionCostCents}¢</TableCell>
                      <TableCell align="right">
                        <Chip label={`${explanation.creditsPerSection} credit${explanation.creditsPerSection !== 1 ? 's' : ''}`} size="small"
                          color={explanation.creditsPerSection <= 2 ? 'warning' : 'error'} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="info" sx={{ mb: 1.5 }}>
                <Typography variant="caption" component="div">
                  <strong>Cost is proportional to output text only.</strong> Input tokens (system prompt, proposal context,
                  user instruction) are not billed — only the AI-generated output counts. Credits still round up (ceiling),
                  so a generation costing 1.1¢ charges 2 credits. To minimise rounding waste, set <em>words per credit</em> so
                  that common section lengths divide evenly (e.g. 400 words/credit for 800-word sections = exactly 2 credits).
                </Typography>
              </Alert>

              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="caption">
                  <strong>1¢ per credit ($0.01)</strong> — each credit costs the user one US cent. A full proposal with
                  10 sections costs ~{10 * explanation.creditsPerSection} credits = {fmtUSD(10 * explanation.creditsPerSection * explanation.creditValueCents)}.
                  Adjust the price and words-per-credit ratio in the <strong>Credit Value</strong> tab.
                </Typography>
              </Alert>
            </Paper>
          )}
        </Stack>
      )}

      {/* ── Users ── */}
      {tab === 1 && (() => {
        const handleSort = (field) => {
          if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
          } else {
            setSortField(field);
            setSortDir(field === 'has_verified_org' ? 'desc' : 'desc');
          }
        };

        const SORT_VAL = {
          has_verified_org: u => u.has_verified_org ? 1 : 0,
          balance:          u => u.balance,
          total_purchased:  u => u.total_purchased,
          total_granted:    u => u.total_granted,
          total_used:       u => u.total_used,
          total_revoked:    u => u.total_revoked || 0,
        };

        const sortedUsers = sortField
          ? [...users].sort((a, b) => {
              const av = SORT_VAL[sortField](a);
              const bv = SORT_VAL[sortField](b);
              return sortDir === 'asc' ? av - bv : bv - av;
            })
          : users;

        const usersWithCredits = sortedUsers.filter(u => u.balance > 0);
        const allWithCreditsSelected = usersWithCredits.length > 0 &&
          usersWithCredits.every(u => selectedUsers.has(u.user_id));
        const someSelected = selectedUsers.size > 0;

        const SortHeader = ({ field, align = 'right', children }) => (
          <TableCell align={align} sortDirection={sortField === field ? sortDir : false}>
            <TableSortLabel
              active={sortField === field}
              direction={sortField === field ? sortDir : 'desc'}
              onClick={() => handleSort(field)}
            >
              {children}
            </TableSortLabel>
          </TableCell>
        );

        const toggleUser = (userId) => setSelectedUsers(prev => {
          const next = new Set(prev);
          next.has(userId) ? next.delete(userId) : next.add(userId);
          return next;
        });

        const toggleAllWithCredits = () => setSelectedUsers(
          allWithCreditsSelected ? new Set() : new Set(usersWithCredits.map(u => u.user_id))
        );

        const revokedCredits  = overview?.total_credits_revoked || 0;
        const revokedCount    = overview?.revoked_count || 0;
        const totalIssued     = overview?.total_credits_issued || 0;
        const aiUsed          = overview?.total_credits_consumed || 0;
        const netOutstanding  = totalIssued - aiUsed - revokedCredits;
        const creditValueCents = cfgData?.credit_value_cents ?? 1;
        const creditsToUSD = (n) => fmtUSD(Math.abs(n) * creditValueCents);

        const issuanceRows = [
          {
            label: 'Paid purchases',
            sub:   'Bank transfer',
            count: overview?.paid_count || 0,
            credits: overview?.paid_credits || 0,
            color: 'success.main',
          },
          {
            label: 'Welcome bonuses',
            sub:   'SIGNUP_BONUS grants',
            count: overview?.signup_bonus_count || 0,
            credits: overview?.signup_bonus_credits || 0,
            color: 'info.main',
          },
          {
            label: 'Admin grants',
            sub:   'Bonus / adjustment / refund',
            count: (overview?.other_bonus_count || 0) + (overview?.adjustment_count || 0),
            credits: (overview?.other_bonus_credits || 0) + (overview?.adjustment_credits || 0),
            color: 'primary.main',
          },
          {
            label: 'AI usage',
            sub:   'Credits consumed by AI generation',
            count: null,
            credits: -aiUsed,
            color: 'error.main',
          },
          {
            label: 'Revoked',
            sub:   'Admin revoke / reset bonus / revoke-all',
            count: revokedCount || null,
            credits: -revokedCredits,
            color: 'warning.main',
          },
        ];

        return (
          <Stack spacing={2}>
            {/* ── Credit Issuance Summary ── */}
            {overview && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Credit Issuance Summary</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Transactions</TableCell>
                        <TableCell align="right">Credits</TableCell>
                        <TableCell align="right">USD Value</TableCell>
                        <TableCell align="right">Running Total</TableCell>
                        <TableCell align="right">Running USD</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {issuanceRows.filter(r => r.credits !== 0 || r.count).map((r, idx) => {
                        const running = issuanceRows
                          .slice(0, idx + 1)
                          .reduce((s, x) => s + x.credits, 0);
                        return (
                          <TableRow key={r.label} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500} color={r.color}>{r.label}</Typography>
                              <Typography variant="caption" color="text.secondary">{r.sub}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary' }}>
                              {r.count !== null ? r.count.toLocaleString() : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={700} color={r.color}>
                                {r.credits >= 0 ? '+' : ''}{r.credits.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color={r.color} sx={{ fontSize: '0.78rem' }}>
                                {r.credits >= 0 ? '+' : '−'}{creditsToUSD(r.credits)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                              {running.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                              {creditsToUSD(running)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell><Typography variant="body2" fontWeight={700}>Net Outstanding</Typography></TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell align="right">
                          <Chip
                            label={netOutstanding.toLocaleString()}
                            size="small"
                            color={netOutstanding > 0 ? 'primary' : 'default'}
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={creditsToUSD(netOutstanding)}
                            size="small"
                            color={netOutstanding > 0 ? 'primary' : 'default'}
                            variant="outlined"
                            sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                placeholder="Search by name or email…"
                value={search}
                onChange={e => { setSearch(e.target.value); setSelectedUsers(new Set()); }}
                size="small"
                sx={{ maxWidth: 400 }}
              />
              {someSelected && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<RevokeIcon />}
                  onClick={() => setBulkRevokeOpen(true)}
                >
                  Revoke from {selectedUsers.size} selected
                </Button>
              )}
              {someSelected && (
                <Button size="small" onClick={() => setSelectedUsers(new Set())}>
                  Clear selection
                </Button>
              )}
            </Stack>

            {usersLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Tooltip title={allWithCreditsSelected ? 'Deselect all' : 'Select all with credits'}>
                          <Checkbox
                            size="small"
                            checked={allWithCreditsSelected}
                            indeterminate={someSelected && !allWithCreditsSelected}
                            onChange={toggleAllWithCredits}
                            disabled={usersWithCredits.length === 0}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <SortHeader field="has_verified_org" align="center">Org</SortHeader>
                      <SortHeader field="balance">Balance</SortHeader>
                      <SortHeader field="total_purchased">Total Purchased</SortHeader>
                      <SortHeader field="total_granted">Total Granted</SortHeader>
                      <SortHeader field="total_used">Total Used</SortHeader>
                      <SortHeader field="total_revoked">Total Revoked</SortHeader>
                      <TableCell>Last Transaction</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center">
                          <Typography variant="body2" color="text.secondary" py={2}>No users found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : sortedUsers.map(u => {
                      const isSelected = selectedUsers.has(u.user_id);
                      return (
                        <TableRow
                          key={u.user_id}
                          hover
                          selected={isSelected}
                          sx={isSelected ? { bgcolor: 'error.50' } : undefined}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => toggleUser(u.user_id)}
                              disabled={u.balance <= 0}
                            />
                          </TableCell>
                          <TableCell>{u.name}</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{u.email}</TableCell>
                          <TableCell align="center">
                            <Tooltip title={u.has_verified_org ? 'Has verified organization membership' : 'No verified organization — credits cannot be granted'}>
                              {u.has_verified_org
                                ? <VerifiedOrgIcon fontSize="small" sx={{ color: 'success.main' }} />
                                : <UnverifiedOrgIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={u.balance}
                              size="small"
                              color={u.balance > 0 ? 'success' : u.balance === 0 ? 'default' : 'error'}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>+{u.total_purchased}</TableCell>
                          <TableCell align="right" sx={{ color: u.total_granted > 0 ? 'info.main' : 'text.secondary' }}>
                            {u.total_granted > 0 ? `+${u.total_granted}` : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>
                            {u.total_used > 0 ? `-${u.total_used}` : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: (u.total_revoked || 0) > 0 ? 'warning.main' : 'text.secondary' }}>
                            {(u.total_revoked || 0) > 0 ? `-${u.total_revoked}` : '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {u.last_transaction ? (
                              <>
                                <Chip label={u.last_transaction.type} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                                {u.last_transaction.amount > 0 ? '+' : ''}{u.last_transaction.amount}
                                {' · '}{new Date(u.last_transaction.created).toLocaleDateString()}
                              </>
                            ) : '—'}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title={u.has_verified_org ? 'Grant credits' : 'Cannot grant — user has no verified organization membership'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => setGrantTarget(u)}
                                    disabled={!u.has_verified_org}
                                  >
                                    <GrantIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={u.balance > 0 ? 'Revoke credits' : 'No credits to revoke'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setRevokeTarget(u)}
                                    disabled={u.balance <= 0}
                                  >
                                    <RevokeIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        );
      })()}

      {/* ── Pending Purchases ── */}
      {tab === 2 && (
        pendingLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Reference Code</TableCell>
                  <TableCell>Package</TableCell>
                  <TableCell align="right">Credits</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No pending purchases — all transfers have been reviewed.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : pending.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell fontWeight={500}>{p.user_name}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{p.user_email}</TableCell>
                    <TableCell>
                      <Typography variant="mono" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {p.payment_intent_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{p.package_id}</TableCell>
                    <TableCell align="right"><strong>{p.credits}</strong></TableCell>
                    <TableCell align="right">{fmt(p.amount_cents)}</TableCell>
                    <TableCell><StatusChip status={p.status} /></TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{new Date(p.created).toLocaleDateString()}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Confirm — add credits to user">
                          <IconButton size="small" color="success" onClick={() => handleConfirm(p.id)}>
                            <ConfirmIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject transfer">
                          <IconButton size="small" color="error" onClick={() => handleReject(p.id)}>
                            <RejectIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* ── Pricing & Packages ── */}
      {tab === 3 && (
        cfgLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <Stack spacing={3}>
            {/* Token explanation */}
            {explanation && (
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                  <InfoIcon color="info" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>What users are told about 1 credit</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Based on the rates below, <strong>1 credit (={explanation.creditValueUSD})</strong> covers approximately{' '}
                  <strong>{explanation.wordsPerCredit.toLocaleString()} words</strong> of AI output
                  (~{explanation.pages} academic page{parseFloat(explanation.pages) !== 1 ? 's' : ''},{' '}
                  {explanation.outputTokens.toLocaleString()} output tokens).
                  The raw API cost per generation is <strong>~{explanation.costCents}¢</strong> → rounded up to{' '}
                  <strong>{explanation.creditValueCents}¢ per credit</strong>.
                  Changing the rates below recalculates everything automatically.
                </Typography>
              </Paper>
            )}

            {/* Pricing settings */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={2}>AI Token Pricing</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Credit value (US cents)"
                  type="number"
                  helperText={`1 credit = ${cfg ? fmtUSD(cfg.credit_value_cents) : '?'}`}
                  value={cfg?.credit_value_cents ?? ''}
                  onChange={e => { initConfigEdit(); setConfigDraft(d => ({ ...d, credit_value_cents: parseInt(e.target.value) || 0 })); }}
                  fullWidth
                  slotProps={{ htmlInput: { min: 1 } }}
                />
                <TextField
                  label="Input cost per 1M tokens (cents)"
                  type="number"
                  helperText="GPT-4o: 250 (= $2.50/1M)"
                  value={cfg?.input_cost_per_m_cents ?? ''}
                  onChange={e => { initConfigEdit(); setConfigDraft(d => ({ ...d, input_cost_per_m_cents: parseInt(e.target.value) || 0 })); }}
                  fullWidth
                  slotProps={{ htmlInput: { min: 1 } }}
                />
                <TextField
                  label="Output cost per 1M tokens (cents)"
                  type="number"
                  helperText="GPT-4o: 1000 (= $10.00/1M)"
                  value={cfg?.output_cost_per_m_cents ?? ''}
                  onChange={e => { initConfigEdit(); setConfigDraft(d => ({ ...d, output_cost_per_m_cents: parseInt(e.target.value) || 0 })); }}
                  fullWidth
                  slotProps={{ htmlInput: { min: 1 } }}
                />
              </Stack>
            </Paper>

            {/* Credit packages */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={600}>Credit Packages</Typography>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Set all package credits proportional to their price ÷ credit value">
                    <Button
                      size="small"
                      startIcon={<RecalcIcon />}
                      color="warning"
                      onClick={() => {
                        initConfigEdit();
                        setConfigDraft(d => ({
                          ...d,
                          packages: (d?.packages || []).map(pkg => ({
                            ...pkg,
                            credits: proportionalCredits(pkg.price_cents, d?.credit_value_cents ?? cfg?.credit_value_cents ?? 1),
                          })),
                        }));
                      }}
                    >
                      Recalculate All
                    </Button>
                  </Tooltip>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      initConfigEdit();
                      const defaultPrice = 999;
                      setConfigDraft(d => ({
                        ...d,
                        packages: [...(d?.packages || []), {
                          id: `pkg_${Date.now()}`,
                          label: 'New Package',
                          credits: proportionalCredits(defaultPrice, d?.credit_value_cents ?? cfg?.credit_value_cents ?? 1) || 50,
                          price_cents: defaultPrice,
                          original_price_cents: undefined,
                          currency: 'eur',
                          description: '',
                          highlight: false,
                        }],
                      }));
                    }}
                  >
                    Add Package
                  </Button>
                </Stack>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Credits are auto-calculated proportional to sale price ÷ credit value. Set <strong>Original Price</strong> to activate a discount — users see the original price struck through.
                The <RecalcIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> icon appears when credits diverge from the proportional value.
              </Typography>
              {(cfg?.packages || []).map((pkg, i) => (
                <PackageRow
                  key={i}
                  pkg={pkg}
                  creditValueCents={cfg?.credit_value_cents ?? 1}
                  onChange={updated => {
                    initConfigEdit();
                    setConfigDraft(d => ({
                      ...d,
                      packages: d.packages.map((p, j) => j === i ? updated : p),
                    }));
                  }}
                  onDelete={() => {
                    initConfigEdit();
                    setConfigDraft(d => ({
                      ...d,
                      packages: d.packages.filter((_, j) => j !== i),
                    }));
                  }}
                />
              ))}
              {(!cfg?.packages || cfg.packages.length === 0) && (
                <Typography variant="body2" color="text.secondary">No packages defined. Add one above.</Typography>
              )}
            </Paper>

            <Box>
              <Button
                variant="contained"
                startIcon={savingConfig ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSaveConfig}
                disabled={savingConfig || !configDraft}
              >
                Save Configuration
              </Button>
              {configDraft && (
                <Button sx={{ ml: 1 }} onClick={() => setConfigDraft(null)}>Discard changes</Button>
              )}
            </Box>
          </Stack>
        )
      )}

      {/* ── Credit Value ── */}
      {tab === 4 && (
        cfgLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (() => {
          const words = cfg?.words_per_credit ?? 500;
          const outputRate = cfg?.output_cost_per_m_cents ?? 1000;
          const { outputTokens, costCents, recommendedCents } = calcCreditValue({
            wordsPerCredit: words,
            outputRateCents: outputRate,
          });
          const currentMatches = cfg?.credit_value_cents === recommendedCents;

          return (
            <Stack spacing={3}>
              {/* Plain-language summary */}
              <Paper variant="outlined" sx={{ p: 3, bgcolor: 'primary.50', borderColor: 'primary.200' }}>
                <Typography variant="h5" fontWeight={700} mb={0.5}>
                  1 credit ≈ <Box component="span" color="primary.main">{words.toLocaleString()} words</Box> of AI output
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This is roughly one abstract section or a short methods paragraph. Users see this when they hover
                  over credit costs in the proposal editor.
                </Typography>
              </Paper>

              {/* Slider */}
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>Words per credit</Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">{words.toLocaleString()}</Typography>
                </Stack>
                <Slider
                  value={words}
                  min={100}
                  max={2000}
                  step={50}
                  marks={[
                    { value: 100,  label: '100' },
                    { value: 500,  label: '500 words\n(abstract)' },
                    { value: 1000, label: '1 000' },
                    { value: 2000, label: '2 000' },
                  ]}
                  valueLabelDisplay="auto"
                  onChange={(_, v) => {
                    initConfigEdit();
                    setConfigDraft(d => ({ ...d, words_per_credit: v }));
                  }}
                  sx={{ mt: 1, mb: 2 }}
                />
                <Stack direction="row" spacing={2} mt={2}>
                  {[100, 250, 500, 750, 1000, 1500].map(v => (
                    <Button
                      key={v}
                      size="small"
                      variant={words === v ? 'contained' : 'outlined'}
                      onClick={() => { initConfigEdit(); setConfigDraft(d => ({ ...d, words_per_credit: v })); }}
                    >
                      {v}
                    </Button>
                  ))}
                </Stack>
              </Paper>

              {/* Token calculation breakdown */}
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>How this translates to tokens & cost</Typography>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Output tokens ({words} words × 1.33 tokens/word)
                    </Typography>
                    <Chip label={`~${outputTokens.toLocaleString()} tokens`} size="small" color="info" variant="outlined" />
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack>
                      <Typography variant="body2" color="text.secondary">
                        API cost — output tokens only ({fmtUSD(outputRate)}/1M output)
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Input tokens are not charged — cost is proportional to generated text only
                      </Typography>
                    </Stack>
                    <Chip label={`~${costCents.toFixed(4)}¢ per generation`} size="small" color="warning" variant="outlined" />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack>
                      <Typography variant="body2" fontWeight={600}>Recommended credit value</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Rounded up so 1 credit always covers {words} words
                      </Typography>
                    </Stack>
                    <Chip
                      label={`${recommendedCents}¢ per credit (${fmtUSD(recommendedCents)})`}
                      size="small"
                      color={currentMatches ? 'success' : 'error'}
                    />
                  </Stack>
                  {!currentMatches && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Current credit value is {cfg?.credit_value_cents ?? '?'}¢ but {words} words/credit
                      requires {recommendedCents}¢. Apply below to fix this.
                    </Alert>
                  )}
                  {currentMatches && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      Credit value is correctly set to {recommendedCents}¢ for {words} words/credit.
                    </Alert>
                  )}
                </Stack>
              </Paper>

              {/* Save */}
              <Box>
                <Button
                  variant="contained"
                  startIcon={savingConfig ? <CircularProgress size={16} /> : <SaveIcon />}
                  disabled={savingConfig || !configDraft}
                  onClick={async () => {
                    // Apply the recommended credit_value_cents when saving from this tab
                    const finalDraft = { ...configDraft, credit_value_cents: recommendedCents };
                    setSavingConfig(true);
                    try {
                      await axiosService.put('/billing/admin/config/', finalDraft);
                      showSnackbar(`Saved — 1 credit = ${words} words = ${recommendedCents}¢`);
                      setConfigDraft(null);
                      mutateCfg();
                    } catch (err) {
                      showSnackbar(err.response?.data?.error || 'Failed to save', 'error');
                    }
                    setSavingConfig(false);
                  }}
                >
                  Apply & Save
                </Button>
                {configDraft && (
                  <Button sx={{ ml: 1 }} onClick={() => setConfigDraft(null)}>Discard</Button>
                )}
              </Box>
            </Stack>
          );
        })()
      )}

      {/* ── Profit Calculator ── */}
      {tab === 5 && (() => {
        const creditPrice = cfgData?.credit_value_cents ?? 1;
        const words       = cfgData?.words_per_credit ?? 500;
        const outputRate  = cfgData?.output_cost_per_m_cents ?? 1000;
        const inputRate   = cfgData?.input_cost_per_m_cents ?? 250;

        const { outputTokens, costCents: outputCostCents } = calcCreditValue({
          wordsPerCredit: words,
          outputRateCents: outputRate,
        });

        // Input tokens are NOT billed to users but ARE a real platform cost
        const inputCostCents    = profitInputTokens * inputRate / 1_000_000;
        const totalApiCostCents = outputCostCents + inputCostCents;

        const vatCents    = creditPrice * profitVat / 100;
        const netRevenue  = creditPrice - vatCents;
        const grossProfit = netRevenue - totalApiCostCents - profitOverhead;
        const incomeTax   = Math.max(0, grossProfit * profitIncomeTax / 100);
        const netProfit   = grossProfit - incomeTax;
        const margin      = creditPrice > 0 ? (netProfit / creditPrice * 100) : 0;
        const breakEven   = profitVat < 100
          ? (totalApiCostCents + profitOverhead) / (1 - profitVat / 100)
          : null;

        const fmtC = (c) => `${c >= 0 ? '' : '−'}${Math.abs(c).toFixed(4)}¢`;
        const fmtPct = (p) => `${p.toFixed(1)}%`;

        const PnlRow = ({ label, value, sub, bold, color, indent }) => (
          <Stack direction="row" justifyContent="space-between" alignItems="baseline"
            sx={{ pl: indent ? 2 : 0, py: 0.25 }}>
            <Typography variant="body2" fontWeight={bold ? 600 : 400}
              color={color || (indent ? 'text.secondary' : 'text.primary')}>{label}</Typography>
            <Typography variant="body2" fontWeight={bold ? 700 : 400}
              color={value < 0 ? 'error.main' : color || 'text.primary'}>
              {fmtC(value)}{sub ? <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{sub}</Typography> : null}
            </Typography>
          </Stack>
        );

        const volumes = [10, 100, 1_000, 10_000, 100_000];
        const packages = cfgData?.packages || [];

        return (
          <Stack spacing={3}>
            {/* Inputs */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={0.5}>Cost & Tax Assumptions</Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Users are only charged for <strong>output tokens</strong>. Input tokens are a real platform cost —
                enter the estimated average prompt size per generation below.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
                <TextField
                  label="Avg. input tokens per generation"
                  type="number"
                  value={profitInputTokens}
                  onChange={e => setProfitInputTokens(parseInt(e.target.value) || 0)}
                  helperText={`~${fmtUSD(profitInputTokens * inputRate / 100_000)} cost at ${fmtUSD(inputRate)}/1M — system prompt + proposal context + instruction`}
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, step: 100 } }}
                />
                <TextField
                  label="Fixed Overhead per Credit (cents)"
                  type="number"
                  value={profitOverhead}
                  onChange={e => setProfitOverhead(parseFloat(e.target.value) || 0)}
                  helperText="Hosting, support, infra costs per credit (e.g. 0.05)"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="VAT / Sales Tax (%)"
                  type="number"
                  value={profitVat}
                  onChange={e => setProfitVat(parseFloat(e.target.value) || 0)}
                  helperText="Collected from users, remitted to tax authority"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, max: 100, step: 0.5 } }}
                />
                <TextField
                  label="Corporate / Income Tax (%)"
                  type="number"
                  value={profitIncomeTax}
                  onChange={e => setProfitIncomeTax(parseFloat(e.target.value) || 0)}
                  helperText="Applied to gross profit after all API costs"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, max: 100, step: 0.5 } }}
                />
              </Stack>
            </Paper>

            {/* Per-credit P&L */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={2}>Per Credit P&amp;L</Typography>
              <Stack spacing={0.25}>
                <PnlRow label="Sale price" value={creditPrice} bold />
                <PnlRow label={`VAT (${fmtPct(profitVat)})`} value={-vatCents} indent sub="→ remitted to govt" />
                <PnlRow label="Net revenue" value={netRevenue} bold />
                <Divider sx={{ my: 0.75 }} />
                <PnlRow label={`Output tokens cost (${outputTokens.toLocaleString()} tokens — billed to user)`} value={-outputCostCents} indent />
                <PnlRow label={`Input tokens cost (~${profitInputTokens.toLocaleString()} tokens — billed to platform, not charged to users)`} value={-inputCostCents} indent />
                <PnlRow label="Total API cost" value={-totalApiCostCents} indent bold />
                {profitOverhead > 0 && <PnlRow label="Fixed overhead" value={-profitOverhead} indent />}
                <PnlRow label="Gross profit" value={grossProfit} bold
                  color={grossProfit >= 0 ? 'success.main' : 'error.main'} />
                {profitIncomeTax > 0 && <>
                  <PnlRow label={`Income tax (${fmtPct(profitIncomeTax)})`} value={-incomeTax} indent />
                  <PnlRow label="Net profit" value={netProfit} bold
                    color={netProfit >= 0 ? 'success.main' : 'error.main'} />
                </>}
                <Divider sx={{ my: 0.75 }} />
                <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
                  <Typography variant="body2" fontWeight={700}>Profit margin</Typography>
                  <Chip
                    label={`${margin.toFixed(1)}%`}
                    size="small"
                    color={margin >= 30 ? 'success' : margin >= 0 ? 'warning' : 'error'}
                  />
                </Stack>
              </Stack>

              {breakEven !== null && (
                <Alert
                  severity={creditPrice >= Math.ceil(breakEven) ? 'success' : 'error'}
                  sx={{ mt: 2 }}
                >
                  {creditPrice >= Math.ceil(breakEven)
                    ? `Break-even at ${fmtC(breakEven)} — current price of ${fmtC(creditPrice)} is profitable.`
                    : `Need at least ${fmtC(breakEven)} per credit to break even. Current price (${fmtC(creditPrice)}) is below cost.`}
                </Alert>
              )}
            </Paper>

            {/* Volume projections */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={2}>Volume Projections</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Credits Sold</TableCell>
                      <TableCell align="right">Gross Revenue</TableCell>
                      <TableCell align="right">VAT (remitted)</TableCell>
                      <TableCell align="right">Output Cost</TableCell>
                      <TableCell align="right">Input Cost</TableCell>
                      {profitOverhead > 0 && <TableCell align="right">Overhead</TableCell>}
                      <TableCell align="right">Gross Profit</TableCell>
                      {profitIncomeTax > 0 && <TableCell align="right">Income Tax</TableCell>}
                      <TableCell align="right">Net Profit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {volumes.map(n => {
                      const rev      = creditPrice * n / 100;
                      const vat      = vatCents * n / 100;
                      const outCost  = outputCostCents * n / 100;
                      const inCost   = inputCostCents * n / 100;
                      const ovhd     = profitOverhead * n / 100;
                      const gp       = (grossProfit * n) / 100;
                      const itax     = Math.max(0, gp * profitIncomeTax / 100);
                      const np       = gp - itax;
                      return (
                        <TableRow key={n} hover>
                          <TableCell><strong>{n.toLocaleString()}</strong></TableCell>
                          <TableCell align="right">{fmtUSD(rev * 100)}</TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>−{fmtUSD(vat * 100)}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>−{fmtUSD(outCost * 100)}</TableCell>
                          <TableCell align="right" sx={{ color: 'warning.main' }}>−{fmtUSD(inCost * 100)}</TableCell>
                          {profitOverhead > 0 && <TableCell align="right" sx={{ color: 'error.main' }}>−{fmtUSD(ovhd * 100)}</TableCell>}
                          <TableCell align="right" sx={{ color: gp >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>{fmtUSD(gp * 100)}</TableCell>
                          {profitIncomeTax > 0 && <TableCell align="right" sx={{ color: 'text.secondary' }}>−{fmtUSD(itax * 100)}</TableCell>}
                          <TableCell align="right" sx={{ color: np >= 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>{fmtUSD(np * 100)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                All amounts in USD. VAT column is collected from users then paid to the tax authority — not retained profit.
              </Typography>
            </Paper>

            {/* Per-package profitability */}
            {packages.length > 0 && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} mb={2}>Per Package Profitability</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Package</TableCell>
                        <TableCell align="right">Credits</TableCell>
                        <TableCell align="right">Sale Price</TableCell>
                        <TableCell align="right">VAT</TableCell>
                        <TableCell align="right">Net Revenue</TableCell>
                        <TableCell align="right">Output Cost</TableCell>
                        <TableCell align="right">Input Cost</TableCell>
                        <TableCell align="right">Gross Profit</TableCell>
                        <TableCell align="right">Margin</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {packages.map(pkg => {
                        const saleUSD    = pkg.price_cents / 100;
                        const vatAmt     = saleUSD * profitVat / 100;
                        const netRev     = saleUSD - vatAmt;
                        const outCost    = outputCostCents * pkg.credits / 100;
                        const inCost     = inputCostCents * pkg.credits / 100;
                        const apiCost    = totalApiCostCents * pkg.credits / 100;
                        const ovhd       = profitOverhead * pkg.credits / 100;
                        const gp         = netRev - apiCost - ovhd;
                        const pkgMgn   = saleUSD > 0 ? (gp / saleUSD * 100) : 0;
                        return (
                          <TableRow key={pkg.id} hover>
                            <TableCell>
                              <Stack>
                                <Typography variant="body2" fontWeight={600}>{pkg.label}</Typography>
                                {pkg.description && <Typography variant="caption" color="text.secondary">{pkg.description}</Typography>}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">{pkg.credits}</TableCell>
                            <TableCell align="right">{fmt(pkg.price_cents)}</TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary' }}>−{fmtUSD(vatAmt * 100)}</TableCell>
                            <TableCell align="right">{fmtUSD(netRev * 100)}</TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>−{fmtUSD(outCost * 100)}</TableCell>
                            <TableCell align="right" sx={{ color: 'warning.main' }}>−{fmtUSD(inCost * 100)}</TableCell>
                            <TableCell align="right" sx={{ color: gp >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                              {fmtUSD(gp * 100)}
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={`${pkgMgn.toFixed(1)}%`} size="small"
                                color={pkgMgn >= 30 ? 'success' : pkgMgn >= 0 ? 'warning' : 'error'} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {overview && (
              <Alert severity="info" variant="outlined" sx={{ py: 1 }}>
                <Typography variant="caption">
                  Credit issuance breakdown has moved to the <strong>Users</strong> tab.
                  Net outstanding: <strong>{(
                    (overview.total_credits_issued || 0) -
                    (overview.total_credits_consumed || 0) -
                    (overview.total_credits_revoked || 0)
                  ).toLocaleString()}</strong> credits
                  ({overview.total_credits_issued?.toLocaleString()} issued −{' '}
                  {overview.total_credits_consumed?.toLocaleString()} used −{' '}
                  {(overview.total_credits_revoked || 0).toLocaleString()} revoked).
                </Typography>
              </Alert>
            )}
          </Stack>
        );
      })()}

      {/* Grant dialog */}
      {grantTarget && (
        <GrantDialog
          open={Boolean(grantTarget)}
          user={grantTarget}
          onClose={() => setGrantTarget(null)}
          onSuccess={() => { mutateUsers(); mutate('/billing/admin/overview/'); }}
        />
      )}

      {/* Revoke dialog */}
      {revokeTarget && (
        <RevokeDialog
          open={Boolean(revokeTarget)}
          user={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onSuccess={() => { mutateUsers(); mutate('/billing/admin/overview/'); }}
        />
      )}

      {/* Bulk revoke dialog */}
      {bulkRevokeOpen && (
        <BulkRevokeDialog
          open={bulkRevokeOpen}
          users={users.filter(u => selectedUsers.has(u.user_id))}
          onClose={() => setBulkRevokeOpen(false)}
          onSuccess={() => {
            setSelectedUsers(new Set());
            mutateUsers();
            mutate('/billing/admin/overview/');
          }}
        />
      )}

      {/* Reset signup bonus dialog */}
      <ResetBonusDialog
        open={resetBonusOpen}
        onClose={() => setResetBonusOpen(false)}
        onSuccess={() => { mutateUsers(); mutate('/billing/admin/overview/'); }}
      />

      {/* Full credit reset dialog */}
      <FullResetDialog
        open={fullResetOpen}
        onClose={() => setFullResetOpen(false)}
        onSuccess={() => { mutateUsers(); mutate('/billing/admin/overview/'); }}
      />
    </PageContainer>
  );
};

export default AdminAICreditsPage;
